import * as React from 'react'
import { NextPage } from 'next'
import { Template } from '@/components/common/Template'
import { css } from '@emotion/react'
import { colors, font, mq } from 'plume-ui'
import Link from 'next/link'
import { IncomingMessage, ServerResponse } from 'http'
import { useEffect } from 'react'
import { ERROR } from '@/lib/tracking'
import { pushEventToGTM } from '@/lib/gtm'
import { useUser } from '@/contexts/AuthContext'
import { IcomoonIcon } from '@/components/atoms/IcomoonIcon'

type Props = {
  statusCode?: number
  err?: any
}

type Msg = {
  statusCodeForDisplay?: number
  title?: string
  description?: string
  image?: string
}

const Error: NextPage<Props> = ({ statusCode = 404, err }) => {
  const msg = msgOf(statusCode)
  const user = useUser()

  useEffect(() => {
    const errorMessage = `${err?.name} ${err?.message}`
    console.log('error', errorMessage)
    pushEventToGTM(user, ERROR, errorMessage)
  }, [])

  return (
    <Template>
      <div
        css={css`
          text-align: center;
          color: ${colors.black30};
          padding: 0 15px;
        `}
      >
        <div
          id="error-page-error-code"
          css={css`
            font-size: 100px;
            font-weight: ${font.weight.normal};
          `}
        >
          {msg.statusCodeForDisplay}
        </div>
        <div
          css={css`
            font-size: 28px;
            font-weight: ${font.weight.normal};
          `}
        >
          {msg.title}
        </div>
        {msg.image && (
          <img
            src={msg.image}
            alt="Error Image"
            css={css`
              margin-top: 40px;
              ${mq.large} {
                margin-top: 64px;
              }
              width: 240px;
              ${mq.large} {
                width: 320px;
              }
            `}
          />
        )}
        <div
          css={css`
            margin-top: 40px;
            color: ${colors.textLight};
            font-size: 16px;
            ${mq.large} {
              font-size: 18px;
            }
          `}
          data-test-id="error-page-description"
        >
          {msg.description}
        </div>
        <Link href="/">
          <a
            css={css`
              margin-top: 40px;
              margin-bottom: 40px;
              ${mq.large} {
                margin-bottom: 64px;
              }
              display: flex;
              align-items: center;
              justify-content: center;
            `}
          >
            <IcomoonIcon
              name="arrow-back"
              css={css`
                color: ${colors.link};
                font-size: 14px;
                padding-right: 4px;
              `}
            ></IcomoonIcon>
            <span
              css={css`
                color: ${colors.link};
                font-size: 14px;
              `}
            >
              トップに戻る
            </span>
          </a>
        </Link>
      </div>
    </Template>
  )
}

const msgOf = (statusCode: number): Msg => {
  switch (statusCode) {
    case 403:
      return {
        title: 'FORBIDDEN',
        description: 'このページへのアクセスは禁止されています。',
      }
    case 404:
      return {
        image: `/static/images/error/illust-404.png`,
        description: 'お探しのページは見つかりませんでした。',
      }
    case 500:
    default:
      return {
        statusCodeForDisplay: 500,
        title: 'INTERNAL SERVER ERROR',
      }
  }
}

const handleTrailingSlash = (req: IncomingMessage, res: ServerResponse) => {
  if (req.url) {
    const urlParts = req.url.split('?')
    if (urlParts[0].endsWith('/')) {
      urlParts[0] = urlParts[0].substring(0, urlParts[0].length - 1)
      res.writeHead(301, { Location: urlParts.join('?') })
      res.end()
    }
  }
}

Error.getInitialProps = async (ctx) => {
  const { req, res, err } = ctx
  const statusCode = res ? res.statusCode : err ? err.statusCode : 404
  if (statusCode === 404 && req && res) {
    handleTrailingSlash(req, res)
  }

  if (res) {
    // Running on the server
    if (statusCode === 404) {
      return { statusCode: 404 }
    }
  }

  return { statusCode, err }
}

export default Error
